import { CSSProperties, Fragment, memo, useMemo } from 'react'
import classNames from 'classnames'

import { questionPositionToVP } from '@aytm/app/stats/page/components/lib/utils'
import { customAnswersTitle } from '@aytm/app/stats/lib/util/helpers'

import {
  TableDataViewCell,
  TableDataViewCellTitle,
  TitleCellEvent
} from '@aytm/app/stats/components/data-views/table/types'

import ImagePopup from '@aytm/app/stats/components/image-popup/ImagePopup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/components/data-views/table/styles/Table.module.scss'

const SCREENER_TITLES: Record<string, string> = {
  accepted: 'Qualifies: choosing this answer let the respondent continue',
  ejected: 'Disqualifies: choosing this answer ended the interview',
  indifferent: 'No effect: this answer neither qualified nor disqualified'
}

type TableCellTitleProps = {
  cell: TableDataViewCell
  width: number | string
  position: number
  format?: (value: number | string) => number | string
}

type StylesProps = {
  flexGrow?: number
  flexShrink?: number
  flexBasis?: string
  width?: string
  borderLeftColor?: string
}

export default memo(TableCellTitle)
export function TableCellTitle(props: TableCellTitleProps) {
  const cell = props.cell as TableDataViewCellTitle

  const styles = useMemo(() => {
    const values: StylesProps = {}
    if ('width' in props && Number.isFinite(props.width)) {
      values.flexGrow = 0
      values.flexShrink = 0
      values.flexBasis = `${props.width}px`
      values.width = `${props.width}px`
    }
    if ('width' in props && 'string' == typeof props.width) {
      values.flexGrow = 0
      values.flexShrink = 0
      values.flexBasis = props.width
      values.width = props.width
    }
    if ('color' in cell) {
      values.borderLeftColor = cell.color
    }
    return values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.width, cell.color])

  const renderSkipsTo = () => (
    <>
      {1 == cell.skips_to.length ? null : <i className="icon-aytmfd-random" />}
      {cell.skips_to.map((item, index) => (
        <Fragment key={index + 1}>
          {0 < index ? ' or ' : ''}
          {-1 == item ? (
            <span className={style['row-cell-title-skips_to-exit']}>
              {1 == cell.skips_to.length ? <i className="icon-aytmfd-skip" /> : null}EXIT
            </span>
          ) : -99 == item ? (
            <span className={style['row-cell-title-skips_to-exit']}>
              {1 == cell.skips_to.length ? <i className="icon-aytmfd-skip" /> : null}HIDDEN EXIT
            </span>
          ) : (
            <div
              className={style['row-cell-title-skips_to-item']}
              onClick={cell.onSkipsToClick ? event => cell.onSkipsToClick(event, item) : null}
            >
              {1 == cell.skips_to.length ? <i className="icon-aytmfd-skip" /> : null}
              {questionPositionToVP(item)}
            </div>
          )}
        </Fragment>
      ))}
    </>
  )

  return (
    <div
      className={classNames(
        style['row-cell'],
        style['row-cell-title'],
        'classNames' in cell ? cell.classNames.map(className => style[className]).join(' ') : '',
        { [style['row-cell-title-active']]: cell.onClick, [`color-${cell.colorIndex}`]: 'colorIndex' in cell }
      )}
      title={'tooltip' in cell ? cell.tooltip : null}
      style={styles}
      onClick={cell.onClick ? () => cell.onClick(props.position) : null}
    >
      {'color' in cell || 'colorIndex' in cell ? (
        <span
          className={classNames(style['row-cell-title-swatch'], { [`color-${cell.colorIndex}`]: 'colorIndex' in cell })}
          // a colour named outright publishes itself the way the palette's class does, so the halo
          // has the same property to read whichever way the mark was coloured
          style={
            'color' in cell ? ({ borderLeftColor: cell.color, '--stats-mark': cell.color } as CSSProperties) : null
          }
        />
      ) : null}
      {'image' in cell ? (
        <div className={style['row-cell-title-thumbnail']}>
          <ImagePopup image={cell.image} width={24} height={24} />
        </div>
      ) : null}
      {'screener' in cell ? (
        <span
          className={classNames(
            style['row-cell-title-screener'],
            style[`row-cell-title-screener-${cell.screener.kind}`],
            style[`row-cell-title-screener-${cell.screener.state}`]
          )}
          // the mark is a statement, not a control -- it is drawn as the question's own control
          // rather than mounted as one, so nothing here is focusable, announced as a checkbox, or
          // repainted by the theme on the one element whose colour is the message
          role="img"
          aria-label={SCREENER_TITLES[cell.screener.state]}
          title={SCREENER_TITLES[cell.screener.state]}
        >
          {'cb' === cell.screener.kind ? <span className="p-c pi pi-check" /> : <span className="p-radiobutton-icon" />}
        </span>
      ) : null}
      {'label' in cell ? (
        <div
          className={classNames(style['row-cell-title-label'], {
            [style['row-cell-title-label-active']]: cell.onLabelClick
          })}
          {...pressable(cell.onLabelClick ? (event: TitleCellEvent) => cell.onLabelClick(event, props.position) : null)}
        >
          {cell.label}:
        </div>
      ) : null}
      <div
        className={classNames(style['row-cell-title-text'], {
          [style['row-cell-title-text-active']]: cell.onTextClick
        })}
        {...pressable(cell.onTextClick ? (event: TitleCellEvent) => cell.onTextClick(event, props.position) : null)}
        dangerouslySetInnerHTML={{ __html: cell.title }}
      />
      {'comments' in cell ? (
        <div
          data-testid="stat-table-row-comments"
          className={classNames(style['row-cell-title-comments'], {
            [style['row-cell-title-comments-disabled']]: 0 == cell.comments
          })}
          aria-label={0 < cell.comments && cell.onCommentsClick ? customAnswersTitle(cell.comments) : undefined}
          {...pressable(
            0 < cell.comments && cell.onCommentsClick
              ? (event: TitleCellEvent) => cell.onCommentsClick(event, props.position)
              : null
          )}
        >
          <span className={style['row-cell-title-comments-count']}>{cell.comments}</span>
          <span className={style['row-cell-title-comments-icon']} title={customAnswersTitle(cell.comments)}>
            <i className="icon-aytmfd-comments-v2" />
          </span>
        </div>
      ) : null}
      {'skips_to' in cell ? <div className={style['row-cell-title-skips_to']}>{renderSkipsTo()}</div> : null}
      {'appendix' in cell ? (
        <span className={style['row-cell-title-appendix']} dangerouslySetInnerHTML={{ __html: cell.appendix }} />
      ) : null}
    </div>
  )
}
